import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Skeleton } from '../common/Skeleton';

/**
 * StatCard 컴포넌트
 * 관리자 대시보드 통계 카드
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string|number} props.value
 * @param {React.ComponentType} props.icon - lucide-react 아이콘
 * @param {string} props.color - blue|green|yellow|red|gray|purple
 * @param {string} props.trend - 보조 설명 텍스트 (하위호환)
 * @param {number} props.trendValue - 증감률(%) — 양수=증가/음수=감소/0=유지, 방향 색상·화살표 자동
 * @param {Function} props.onClick - 지정 시 카드 클릭 가능(딥링크)
 * @param {boolean} props.loading - 스켈레톤 표시
 */
const StatCard = ({ title, value, icon: Icon, color = 'blue', trend, trendValue, onClick, loading = false }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    red: 'bg-red-50 border-red-200',
    gray: 'bg-gray-50 border-gray-200',
    purple: 'bg-purple-50 border-purple-200',
  };

  const iconColorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    gray: 'bg-gray-100 text-gray-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  const clickable = typeof onClick === 'function';

  const renderTrend = () => {
    if (typeof trendValue === 'number') {
      const up = trendValue > 0;
      const down = trendValue < 0;
      const TrendIcon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
      const trendColor = up ? 'text-green-600' : down ? 'text-red-600' : 'text-gray-500';
      return (
        <p className={`mt-2 flex items-center gap-1 text-xs font-medium ${trendColor}`}>
          <TrendIcon className="h-3.5 w-3.5" />
          {Math.abs(trendValue)}%{trend ? ` ${trend}` : ' 전기간 대비'}
        </p>
      );
    }
    if (trend) return <p className="mt-2 text-xs text-gray-500">{trend}</p>;
    return null;
  };

  const content = (
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-sm font-medium text-gray-600">{title}</p>
        {loading ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        )}
        {!loading && renderTrend()}
      </div>
      {Icon && (
        <div className={`rounded-full p-3 ${iconColorClasses[color] || iconColorClasses.blue}`}>
          <Icon className="h-6 w-6" />
        </div>
      )}
    </div>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full rounded-lg border-2 p-6 text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
          colorClasses[color] || colorClasses.blue
        }`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`rounded-lg border-2 p-6 ${colorClasses[color] || colorClasses.blue}`}>
      {content}
    </div>
  );
};

export default StatCard;
